/**
 * Rybbit sink (web) — self-hosted product analytics at analytics.carofi.app.
 *
 * Replaces Amplitude. The script is loaded once by the app's tracking provider;
 * everything here is a no-op until it is on the page, so a blocked or slow
 * script never throws into a click handler.
 *
 * PAGEVIEWS ARE NOT SENT FROM HERE. Rybbit derives them from the URL. The old
 * wrapper hand-wrote a `pageview:${pageId}` event per route, which double-counts
 * every navigation and re-encodes the route table as a list of strings someone
 * has to maintain.
 */

import type { EventName, EventMap, GlobalProperties } from "./events";

type RybbitApi = {
  event: (name: string, properties?: Record<string, unknown>) => void;
  identify: (userId: string) => void;
  clearUserId: () => void;
};

declare global {
  interface Window {
    rybbit?: RybbitApi;
  }
}

const api = (): RybbitApi | undefined =>
  typeof window === "undefined" ? undefined : window.rybbit;

let globals: Partial<GlobalProperties> = {};

/** Set once at init. Merged into every event so call sites never repeat them. */
export const setGlobalProperties = (next: Partial<GlobalProperties>) => {
  globals = { ...globals, ...next };
};

/**
 * DEFENCE IN DEPTH FOR THE PII RULE IN events.ts.
 *
 * The taxonomy already makes a form-object spread a compile error — every
 * payload type is a closed shape of ids, enums and numbers. This exists because
 * a compile-time guarantee only covers call sites that go through this module
 * and stay type-checked: a stray `as any`, a JS call site, or a future event
 * whose property is populated from a form field would all slip past it.
 *
 * The code being replaced shipped entire lead forms — names, phones, emails,
 * national IDs — into the analytics store, so this is not a hypothetical.
 *
 * Keys are matched case-insensitively on a substring, which is deliberately
 * blunt: dropping a legitimate property is a missing chart, while keeping a
 * personal one is a data-protection problem in a 24-month store that also holds
 * session recordings. If a real property is being dropped, RENAME THE PROPERTY
 * rather than narrowing this list.
 */
const PII_KEY_PATTERNS = [
  "name",
  "email",
  "phone",
  "mobile",
  "national",
  "nid",
  "ssn",
  "address",
  "birth",
  "dob",
  "password",
  "token",
  "otp",
  "iban",
  "card",
  "account_number",
];

const isPIIKey = (key: string): boolean => {
  const k = key.toLowerCase();
  // `*_name` fields that are known-safe identifiers, not people. Without this,
  // the blunt "name" match would eat brand_name and merchant_name.
  if (/^(brand|model|merchant|showroom|car|report|service|setting|list|form|screen|section|step|field|entity)_name$/.test(k)) {
    return false;
  }
  return PII_KEY_PATTERNS.some(p => k.includes(p));
};

export const stripPII = (props: Record<string, unknown>): Record<string, unknown> => {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(props)) {
    if (v === undefined) continue; // never send empty keys
    if (isPIIKey(k)) {
      if (process.env.NODE_ENV !== "production") {
        console.warn(`[tracking] dropped property "${k}": looks like personal data. See the PII rule in events.ts.`);
      }
      continue;
    }
    out[k] = v;
  }
  return out;
};

export const rybbitTrack = <E extends EventName>(event: E, properties: EventMap[E]) => {
  try {
    api()?.event(event, stripPII({ ...globals, ...(properties as Record<string, unknown>) }));
  } catch (error) {
    console.error("Rybbit: failed to send", event, error);
  }
};

/**
 * Called on login and whenever the user id becomes known.
 *
 * Pass the OPAQUE user id, never an email or phone — Rybbit stores this as the
 * user identifier and it is not covered by stripPII.
 */
export const rybbitIdentify = (userId: string) => {
  try {
    api()?.identify(userId);
  } catch (error) {
    console.error("Rybbit: failed to identify", error);
  }
};

export const rybbitReset = () => {
  try {
    api()?.clearUserId();
  } catch (error) {
    console.error("Rybbit: failed to clear user", error);
  }
};
